/**
 * Shared definition id for the built-in "price from price list" publication rule.
 *
 * Single source of truth referenced by:
 * - the backend rule definition (`publicationPriceFromPriceListUpdateRule.id`)
 * - the backend extraction policy (`PublicationItemExtractionPolicy`)
 * - the web-app extract-items draft rule preview (Step 3 DRAFT card)
 *
 * The FE preview and the backend rule MUST reference this exact symbol so the
 * previewed rule cannot drift from the rule the policy actually creates.
 */
export const PUBLICATION_PRICE_FROM_PRICE_LIST_UPDATE_RULE_DEFINITION_ID =
  'builtIn-publication-price-from-price-list-update';

// ─────────────────────────────────────────────────────────────────────────────
// Rules HIERARCHY model — single source of truth shared by the backend hierarchy
// walk (`findRulesToExecute` in the server `rules-utils.ts`) and the web-app
// inheritance resolver (`resolveRulesForScope.ts`). Previously hand-copied in three
// places and kept in sync "by hand" — a desync hazard.
//
// These live in `constants.ts` ON PURPOSE: it is the jsonschema-free module behind
// the `@bett3r-dev/teselly-domain/automation/constants` subpath export, so the
// web-app can import these VALUES without pulling the barrel (`index.ts`), which
// re-exports every `*.types.ts` and would drag in the CJS-only
// `@bett3r-dev/jsonschema-definer` (no `default` export under Vite). The server
// imports them from the barrel; the web-app from the `/automation/constants` subpath.
// ─────────────────────────────────────────────────────────────────────────────

// Hierarchy path step shapes — the object `findRulesToExecute` / `dispatchOrderRules`
// pass as `hierarchyTraversal`. `channel` and `integrationUserId` both carry the
// SalesChannelIntegration id (`buildIntegrationId(connectorId, integrationUserId)`),
// duplicated for back-compat with legacy rules stored at
// `hierarchyLevel: 'integrationUserId'`.
export type PublicationHierarchyPath = {
  account: string;
  publication: string;
  channel?: string;
  integrationUserId?: string;
};

export type ItemHierarchyPath = {
  account: string;
  warehouse: string;
  item: string;
};

// Order-level rules (warehouse selection, logistics provider, invoice) act on the
// whole order, so the traversal carries a single `order` path per order.
export type OrderHierarchyPath = {
  account: string;
  order: string;
  channel?: string;
  integrationUserId?: string;
};

// A purchase order is a commitment to BUY, issued to one supplier. Like an order it is
// acted on as a whole, so the traversal carries a single `purchaseOrder` path per PO
// (TV1-2337).
export type PurchaseOrderHierarchyPath = {
  account: string;
  purchaseOrder: string;
};

export type HierarchyTraversal = {
  publication?: PublicationHierarchyPath[];
  item?: ItemHierarchyPath[];
  order?: OrderHierarchyPath[];
  purchaseOrder?: PurchaseOrderHierarchyPath[];
};

// Most specific → least specific. Specific rules override general ones. steps[0] is
// the most specific entity, used as the result grouping key.
export const HIERARCHY_PRECEDENCE: Record<string, string[]> = {
  publication: [ 'publication', 'channel', 'integrationUserId', 'account' ],
  item: [ 'item', 'warehouse', 'account' ],
  // Order-level rules react to an order event and act on the order as a whole — NOT
  // per publication line. The dispatcher (`dispatchOrderRules`) builds a SINGLE order
  // path per order, so the most-specific step `order` is the grouping key and the rule
  // fires exactly once. `channel` precedes `integrationUserId` (mirroring the
  // `publication` chain): the order built-ins are stored at `hierarchyLevel: 'channel'`,
  // and the Reglas catalog exposes a `channel` scope node (not an `integrationUserId`
  // one), so `channel` must be the channel node's own-level for these rules to render
  // as "own"/editable. Both carry the same SalesChannelIntegration id today.
  order: [ 'order', 'channel', 'integrationUserId', 'account' ],
  // Purchase-order rules (TV1-2337) act on the whole PO. The dispatching policy builds a
  // SINGLE path per PO, so `purchaseOrder` is the grouping key and the rule fires exactly
  // once. No `channel`/`integrationUserId` step: a PO is issued to a SUPPLIER, it has no
  // sales-channel dimension at all.
  purchaseOrder: [ 'purchaseOrder', 'account' ]
};

// Override-group identity is case- and whitespace-insensitive. "Stock Update",
// "stock-update " and "STOCK UPDATE" must collide, otherwise users typing the same
// name with different casing would silently produce non-overriding rules.
export const normalizeOverrideGroup = ( raw: string ): string => raw.trim().toLowerCase();

// ─────────────────────────────────────────────────────────────────────────────
// Override groups by EFFECT (TV1-2455, ADR-203)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * An override group as a Rule Definition declares it (`definitionOverrideGroups` entry).
 * Structurally the `RuleDefined` event's `{ id, name }` shape, restated here because this
 * module must stay free of the jsonschema imports that shape is built from.
 */
export type OverrideGroupDeclaration = {
  id: string;
  name: { es: string; en: string; pt: string };
};

/**
 * One id per EFFECT, never per trigger (ADR-203). The placement and on-status fixed-warehouse
 * built-ins share it, and so does an AI rule on either decrement lane. Neither the id nor the
 * name names a trigger: the id is persisted on `RuleSet` / `RuleDefined`, so a trigger in it
 * would need a rename backfill the day groups compare across triggers.
 */
export const WAREHOUSE_STOCK_DECREMENT_OVERRIDE_GROUP: OverrideGroupDeclaration = {
  id: '_WarehouseStockDecrement',
  name: {
    es: 'Descuento de stock del depósito',
    en: 'Warehouse stock decrement',
    pt: 'Baixa de estoque do depósito'
  }
};

/**
 * Stock returned to a warehouse when an order is reversed (`builtIn-order-reversal-stock-restoration`;
 * one id for both OrderCancelled and OrderReturned). Distinct from the decrement id because it is a
 * different effect (ADR-203 rejects per-category grouping for exactly this pair).
 */
export const WAREHOUSE_STOCK_RESTORE_OVERRIDE_GROUP: OverrideGroupDeclaration = {
  id: '_WarehouseStockRestore',
  name: {
    es: 'Restauración de stock del depósito',
    en: 'Warehouse stock restore',
    pt: 'Restauração de estoque do depósito'
  }
};

/**
 * A shipment requested from a logistics provider. Shared by `builtIn-fixed-logistics-provider`
 * and its on-status sibling.
 */
export const SHIPMENT_REQUEST_OVERRIDE_GROUP: OverrideGroupDeclaration = {
  id: '_ShipmentRequest',
  name: {
    es: 'Solicitud de envío',
    en: 'Shipment request',
    pt: 'Solicitação de envio'
  }
};

/**
 * An invoice generated from an order. The id and names predate ADR-203: the two invoicing
 * built-ins declared them inline and they are already persisted, so they are kept verbatim.
 */
export const INVOICE_GENERATION_OVERRIDE_GROUP: OverrideGroupDeclaration = {
  id: '_InvoiceGeneration',
  name: {
    es: 'Generación de Factura',
    en: 'Invoice Generation',
    pt: 'Geração de Fatura'
  }
};

/**
 * The effect group of an AI-authored rule, keyed by its `'<trigger>|<command>'` lane — the
 * same key shape as `COMMAND_CATEGORY` in the ml-and-ai library's `composer/ruleMetadata.ts`.
 * The trigger is part of the key because a command alone is ambiguous (e.g.
 * `RequestSystemsIntegrationsOperation`). A lane absent here carries no group.
 * Pinned against the built-ins by `builtInRules/overrideGroupDrift.test.ts` in the server.
 */
export const OVERRIDE_GROUP_BY_LANE: Readonly<Record<string, OverrideGroupDeclaration>> = {
  'OrderPlaced|UpdateMultipleWarehouseItemsStock': WAREHOUSE_STOCK_DECREMENT_OVERRIDE_GROUP,
  'OrderStatusChanged|UpdateMultipleWarehouseItemsStock': WAREHOUSE_STOCK_DECREMENT_OVERRIDE_GROUP,
  'OrderCancelled|IncreaseMultipleWarehouseItemsStock': WAREHOUSE_STOCK_RESTORE_OVERRIDE_GROUP,
  'OrderReturned|IncreaseMultipleWarehouseItemsStock': WAREHOUSE_STOCK_RESTORE_OVERRIDE_GROUP,
  'OrderPlaced|RequestSystemsIntegrationsOperation': SHIPMENT_REQUEST_OVERRIDE_GROUP,
  'OrderStatusChanged|RequestSystemsIntegrationsOperation': SHIPMENT_REQUEST_OVERRIDE_GROUP,
  'OrderPlaced|GenerateInvoiceFromOrder': INVOICE_GENERATION_OVERRIDE_GROUP,
  'OrderStatusChanged|GenerateInvoiceFromOrder': INVOICE_GENERATION_OVERRIDE_GROUP,
  'BuyerInvoiceIntentRecorded|GenerateInvoiceFromOrder': INVOICE_GENERATION_OVERRIDE_GROUP
};

// ─────────────────────────────────────────────────────────────────────────────
// Rule-execution ATTRIBUTION tag
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The ambient-execution-context tag name a rule execution's own id travels under.
 *
 * Every command a rule dispatches runs inside the worker's `runWithExecutionContext`,
 * and pv3's command handler stamps that context's `tags` onto the metadata of every
 * event those commands emit (nested under `metadata.tags`, never spread). Setting this
 * tag therefore answers "which rule execution produced this event?" for the whole
 * first hop, without touching the GENERATED rules-engine command nodes — they have no
 * per-node metadata seam.
 *
 * FIRST HOP ONLY, deliberately. An event emitted by a POLICY reacting to one of these
 * events runs in a later, separate async context and does NOT inherit the tag.
 * Transitive attribution would need explicit tag propagation on the event-consumption
 * path (a pv3-level change with an allowlist of propagated keys); blanket inheritance
 * would let this id travel arbitrarily far and start reading as causation when it is not.
 *
 * The value is the `executionId`, which is DETERMINISTIC (`uuidv5` over the triggering
 * event id + rule id), so a crash-then-reclaim that re-runs the rule stamps the SAME
 * id — the tag is stable across attempts rather than per-attempt.
 *
 * One constant so the writer (the rules worker) and any reader cannot drift.
 */
export const RULE_EXECUTION_ID_TAG = 'ruleExecutionId';

// The Sub-Goal → rule id derivation. Re-exported HERE so the web app can reach it
// through the jsonschema-free `@bett3r-dev/teselly-domain/automation/constants`
// subpath; the server reaches the same definition through the barrel.
export * from './subGoalRuleId';
export * from './unfilledDeclaredInputs';
